import React, { useState, useEffect } from 'react';
import ConfirmModal from '../components/ConfirmModal';
import Notification from '../components/Notification';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { PageEditor } from '../components/lesson-editors/PageEditors';
import { useNotification } from '../hooks/useNotification';

const LessonEditor = () => {
  const { lessonId, moduleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [lessonInfo, setLessonInfo] = useState({
    title: '',
    description: '',
    lessonType: 'EXPLANATION',
    estimatedTime: null,
    order: 1,
    isActive: true,
    profileImage: '',
  });

  const [lessonData, setLessonData] = useState({
    id: `lesson-${Date.now()}`,
    title: '',
    description: '',
    estimatedTime: null,
    checkpoints: [],
    pages: [],
  });

  const [module, setModule] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pageDeleteModal, setPageDeleteModal] = useState({ isOpen: false, pageIndex: null, pageType: '' });
  const { notification, showError, showSuccess, hideNotification } = useNotification();

  useEffect(() => {
    if (lessonId) {
      fetchLesson();
    } else if (moduleId) {
      fetchModule();
    }
  }, [lessonId, moduleId]);

  const fetchModule = async () => {
    try {
      const response = await api.get(`/admin/courses`);
      const courses = response.data.data.courses || [];
      let foundModule = null;
      
      for (const course of courses) {
        for (const mod of course.modules || []) {
          if (mod.id === moduleId) {
            foundModule = { ...mod, course: course };
            break;
          }
        }
        if (foundModule) break;
      }
      
      if (foundModule) {
        setModule(foundModule);
        const lessons = foundModule.lessons || [];
        setLessonInfo(prev => ({
          ...prev,
          order: lessons.length + 1,
        }));
      }
    } catch (err) {
      console.error('Failed to fetch module:', err);
      setError('Failed to load module information');
    }
  };

  const fetchLesson = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/lessons/${lessonId}`);
      const lesson = response.data.data.lesson;
      
      setLessonInfo({
        title: lesson.title || '',
        description: lesson.description || '',
        lessonType: lesson.lessonType || 'EXPLANATION',
        estimatedTime: lesson.estimatedTime,
        order: lesson.order || 1,
        isActive: lesson.isActive !== false,
        profileImage: lesson.lessonData?.profileImage || '',
      });

      if (lesson.lessonData) {
        setLessonData(lesson.lessonData);
      } else {
        setLessonData({
          id: lesson.id || `lesson-${Date.now()}`,
          title: lesson.title || '',
          description: lesson.description || '',
          estimatedTime: lesson.estimatedTime,
          checkpoints: [],
          pages: [],
        });
      }

      if (lesson.module) {
        setModule({
          id: lesson.module.id,
          title: lesson.module.title,
          course: lesson.module.course,
        });
      }
    } catch (err) {
      console.error('Failed to fetch lesson:', err);
      setError('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        ...lessonInfo,
        lessonData: {
          ...lessonData,
          title: lessonInfo.title,
          description: lessonInfo.description,
          estimatedTime: lessonInfo.estimatedTime,
          profileImage: lessonInfo.profileImage || undefined,
        },
      };

      if (moduleId && !lessonId) {
        payload.moduleId = moduleId;
      }

      let response;
      if (lessonId) {
        response = await api.patch(`/admin/lessons/${lessonId}`, payload);
      } else {
        response = await api.post('/admin/lessons', payload);
      }

      if (response.data.success) {
        setSuccess('Lesson saved successfully!');
        setTimeout(() => {
          if (!lessonId) {
            navigate(`/lessons/${response.data.data.lesson.id}/edit`);
          }
        }, 1500);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error?.message ||
        'Failed to save lesson'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (!lessonId) return;
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!lessonId) return;

    try {
      await api.delete(`/admin/lessons/${lessonId}`);
      setShowDeleteConfirm(false);
      showSuccess('Lesson deleted successfully');
      setTimeout(() => {
        navigate(`/courses`);
      }, 1000);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to delete lesson');
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleDeletePageClick = (index) => {
    const page = lessonData.pages[index];
    setPageDeleteModal({
      isOpen: true,
      pageIndex: index,
      pageType: page.type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    });
  };

  const confirmDeletePage = () => {
    if (pageDeleteModal.pageIndex === null) return;
    
    setLessonData((prev) => {
      const newPages = prev.pages.filter((_, i) => i !== pageDeleteModal.pageIndex);
      // Update page numbers
      const renumberedPages = newPages.map((page, i) => ({
        ...page,
        pageNumber: i,
      }));
      return { ...prev, pages: renumberedPages };
    });
    
    setPageDeleteModal({ isOpen: false, pageIndex: null, pageType: '' });
    showSuccess('Page deleted successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lesson...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {lessonId ? 'Edit Lesson' : 'Create New Lesson'}
          </h1>
          {module && (
            <p className="text-gray-600">
              {module.course?.title} → {module.title}
            </p>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/courses')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          {lessonId && (
            <button
              onClick={handleDeleteClick}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Delete
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Lesson'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {['basic', 'pages', 'checkpoints', 'preview'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'basic' && (
            <BasicInfoTab
              lessonInfo={lessonInfo}
              setLessonInfo={setLessonInfo}
            />
          )}
          {activeTab === 'pages' && (
            <PagesTab
              lessonData={lessonData}
              setLessonData={setLessonData}
              onDeletePageClick={handleDeletePageClick}
              moduleId={moduleId}  
              module={module}
            />
          )}
          {activeTab === 'checkpoints' && (
            <CheckpointsTab
              lessonData={lessonData}
              setLessonData={setLessonData}
            />
          )}
          {activeTab === 'preview' && (
            <PreviewTab lessonData={lessonData} lessonInfo={lessonInfo} />
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Lesson"
        message="Are you sure you want to delete this lesson? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ConfirmModal
        isOpen={pageDeleteModal.isOpen}
        onClose={() => setPageDeleteModal({ isOpen: false, pageIndex: null, pageType: '' })}
        onConfirm={confirmDeletePage}
        title="Delete Page"
        message={`Are you sure you want to delete this ${pageDeleteModal.pageType} page? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          duration={notification.duration}
        />
      )}
    </div>
  );
};

const BasicInfoTab = ({ lessonInfo, setLessonInfo }) => {
  const handleChange = (field, value) => {
    setLessonInfo((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
        <input
          type="text"
          value={lessonInfo.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Enter lesson title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={lessonInfo.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Enter lesson description"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Profile Image URL</label>
        <input
          type="text"
          value={lessonInfo.profileImage || ''}
          onChange={(e) => handleChange('profileImage', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="https://..."
        />
        <p className="text-xs text-gray-500 mt-1">Optional: Profile image for this lesson</p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Lesson Type *</label>
          <select
            value={lessonInfo.lessonType}
            onChange={(e) => handleChange('lessonType', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="EXPLANATION">Explanation</option>
            <option value="AUDIO">Audio</option>
            <option value="INTERACTIVE">Interactive</option>
            <option value="QUIZ">Quiz</option>
            <option value="VIDEO">Video</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Time (minutes)</label>
          <input
            type="number"
            value={lessonInfo.estimatedTime || ''}
            onChange={(e) => handleChange('estimatedTime', e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="45"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
          <input
            type="number"
            value={lessonInfo.order}
            onChange={(e) => handleChange('order', parseInt(e.target.value) || 1)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div className="flex items-center pt-8">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={lessonInfo.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
            />
            <span className="ml-2 text-sm text-gray-700">Active</span>
          </label>
        </div>
      </div>
    </div>
  );
};

const PagesTab = ({ lessonData, setLessonData, onDeletePageClick, moduleId, module }) => {
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [selectedPageType, setSelectedPageType] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const updatePageNumbers = (pages) => {
    return pages.map((page, index) => ({
      ...page,
      pageNumber: index,
    }));
  };

  const addPage = (type, insertPosition = null) => {
    const newPage = {
      id: `page-${Date.now()}`,
      type,
      pageNumber: insertPosition !== null ? insertPosition : lessonData.pages.length,
      estimatedTime: null,
    };

    if (type === 'content') {
      newPage.blocks = [];
    } else if (type === 'virtual-class') {
      newPage.flashcards = [];
      newPage.students = [];
      newPage.steps = [];
      newPage.config = {};
    } else if (type === 'interactive-grid') {
      newPage.grid = {
        mode: 'familiarization',
        type: 'alphabet',
        layout: '5x5',
        columns: 5,
        rows: 5,
        cells: [],
      };
    } else if (type === 'quiz-question') {
      newPage.question = {
        id: `q-${Date.now()}`,
        text: '',
        type: 'multiple-choice',
        points: 1,
      };
      newPage.options = [];
    }

    setLessonData((prev) => {
      let newPages;
      if (insertPosition !== null && insertPosition >= 0 && insertPosition <= prev.pages.length) {
        newPages = [...prev.pages];
        newPages.splice(insertPosition, 0, newPage);
      } else {
        newPages = [...prev.pages, newPage];
      }
      return {
        ...prev,
        pages: updatePageNumbers(newPages),
      };
    });
  };

  const handlePageTypeSelect = (type) => {
    if (type) {
      setSelectedPageType(type);
      setShowPositionModal(true);
    }
  };

  const handlePositionConfirm = (position) => {
    addPage(selectedPageType, position);
    setShowPositionModal(false);
    setSelectedPageType('');
  };

  const movePage = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === lessonData.pages.length - 1) return;

    setLessonData((prev) => {
      const newPages = [...prev.pages];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      
      [newPages[index], newPages[targetIndex]] = [newPages[targetIndex], newPages[index]];
      
      return {
        ...prev,
        pages: updatePageNumbers(newPages),
      };
    });
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setLessonData((prev) => {
      const newPages = [...prev.pages];
      const draggedPage = newPages[draggedIndex];
      
      newPages.splice(draggedIndex, 1);
      
      const newIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
      newPages.splice(newIndex, 0, draggedPage);
      
      return {
        ...prev,
        pages: updatePageNumbers(newPages),
      };
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const updatePage = (index, updates) => {
    setLessonData((prev) => {
      const newPages = [...prev.pages];
      const page = newPages[index];
      const updatedPage = { ...page, ...updates };
      newPages[index] = updatedPage;

      if (updatedPage.type === 'quiz-question' && updates.options) {
        const checkpointId = updatedPage.checkpointId;
        if (checkpointId && updatedPage.id) {
          const correctOption = updatedPage.options?.find((opt) =>
            opt.correct === true || opt.correct === "true"
          );

          const checkpointAnswers = prev.checkpointAnswers ? { ...prev.checkpointAnswers } : {};
          if (!checkpointAnswers[checkpointId]) checkpointAnswers[checkpointId] = {};

          if (correctOption && correctOption.id) {
            checkpointAnswers[checkpointId][updatedPage.id] = correctOption.id;
          } else {
            delete checkpointAnswers[checkpointId][updatedPage.id];
            if (Object.keys(checkpointAnswers[checkpointId]).length === 0) {
              delete checkpointAnswers[checkpointId];
            }
          }

          return { ...prev, pages: newPages, checkpointAnswers };
        }
      }

      return { ...prev, pages: newPages };
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Pages ({lessonData.pages.length})</h3>
        <div className="flex space-x-2">
          <select
            onChange={(e) => handlePageTypeSelect(e.target.value)}
            value=""
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Add Page Type...</option>
            <option value="content">Content</option>
            <option value="virtual-class">Virtual Class</option>
            <option value="interactive-grid">Interactive Grid</option>
            <option value="image">Image</option>
            <option value="checkpoint-directions">Checkpoint Directions</option>
            <option value="quiz-question">Quiz Question</option>
            <option value="quiz-results">Quiz Results</option>
          </select>
        </div>
      </div>

      {/* Position Selection Modal */}
      {showPositionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Where would you like to insert this {selectedPageType.replace('-', ' ')} page?
            </h3>
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              <button
                onClick={() => handlePositionConfirm(0)}
                className="w-full text-left px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                At the beginning (Position 1)
              </button>
              {lessonData.pages.map((page, index) => (
                <button
                  key={page.id}
                  onClick={() => handlePositionConfirm(index + 1)}
                  className="w-full text-left px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  After Page {index + 1} ({page.type.replace('-', ' ')})
                </button>
              ))}
              <button
                onClick={() => handlePositionConfirm(lessonData.pages.length)}
                className="w-full text-left px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                At the end (Position {lessonData.pages.length + 1})
              </button>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowPositionModal(false);
                  setSelectedPageType('');
                }}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {lessonData.pages.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No pages yet. Add a page to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {lessonData.pages.map((page, index) => {
            const finalModuleId = moduleId || module?.id;

            return (
              <div
                key={page.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                className={`transition-all cursor-move ${
                  dragOverIndex === index ? 'opacity-50 border-2 border-primary border-dashed' : ''
                } ${draggedIndex === index ? 'opacity-30' : ''}`}
              >
                <PageEditor
                  page={page}
                  index={index}
                  moduleId={finalModuleId}
                  onUpdate={(updates) => updatePage(index, updates)}
                  onDelete={() => onDeletePageClick(index)}
                  onMoveUp={() => movePage(index, 'up')}
                  onMoveDown={() => movePage(index, 'down')}
                  canMoveUp={index > 0}
                  canMoveDown={index < lessonData.pages.length - 1}
                  checkpoints={lessonData.checkpoints || []}
                  lessonData={lessonData}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CheckpointsTab = ({ lessonData, setLessonData }) => {
  const addCheckpoint = () => {
    const newCheckpoint = {
      id: `checkpoint-${Date.now()}`,
      title: 'Checkpoint',
      icon: { type: 'checkmark', color: 'green', show: true },
      passingScore: 80,
      allowRetake: true,
      maxAttempts: 3,
      timeLimit: null,
    };

    setLessonData((prev) => ({
      ...prev,
      checkpoints: [...prev.checkpoints, newCheckpoint],
    }));
  };

  const updateCheckpoint = (index, updates) => {
    setLessonData((prev) => {
      const newCheckpoints = [...prev.checkpoints];
      newCheckpoints[index] = { ...newCheckpoints[index], ...updates };
      return { ...prev, checkpoints: newCheckpoints };
    });
  };

  const deleteCheckpoint = (index) => {
    setLessonData((prev) => ({
      ...prev,
      checkpoints: prev.checkpoints.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">
          Checkpoints ({lessonData.checkpoints.length})
        </h3>
        <button
          onClick={addCheckpoint}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark"
        >
          Add Checkpoint
        </button>
      </div>

      {lessonData.checkpoints.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No checkpoints yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {lessonData.checkpoints.map((checkpoint, index) => (
            <CheckpointEditor
              key={checkpoint.id}
              checkpoint={checkpoint}
              index={index}
              onUpdate={(updates) => updateCheckpoint(index, updates)}
              onDelete={() => deleteCheckpoint(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CheckpointEditor = ({ checkpoint, index, onUpdate, onDelete }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex justify-between items-start mb-3">
        <h4 className="font-semibold">Checkpoint {index + 1}</h4>
        <button
          onClick={onDelete}
          className="text-red-600 hover:text-red-800 text-sm"
        >
          Delete
        </button>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input
            type="text"
            value={checkpoint.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Passing Score</label>
          <input
            type="number"
            value={checkpoint.passingScore}
            onChange={(e) => onUpdate({ passingScore: parseInt(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>
    </div>
  );
};

const PreviewTab = ({ lessonData, lessonInfo }) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Lesson Preview</h3>
      <div className="bg-gray-50 rounded-lg p-6">
        <pre className="text-xs overflow-auto">
          {JSON.stringify({ ...lessonInfo, lessonData }, null, 2)}
        </pre>
      </div>
    </div>
  );
};

export default LessonEditor;